import { Mutex } from "async-mutex";
import { useAtomCallback } from "jotai/utils";
import { useCallback } from "react";
import { logger } from "../../datadog/logger";
import { CallbackFailure, CallbackSuccess } from "../../types";
import { inSessionPatientInformationAtom } from "../state";
import { RecordingMetadata } from "./types";
import { useGetPresignedUrlCallback } from "./useGetPresignedUrlCallback";

type UploadAudioRecordingToS3Response =
  | CallbackSuccess<Response, UploadAudioCallbackStatusCode.UPLOADED>
  | CallbackFailure<Error, UploadAudioCallbackStatusCode.FAILED_TO_UPLOAD>;

export async function gracefullyUploadAudioRecordingToS3({
  audioBlob,
  presignedUrl,
  appointmentShortId,
  recordingMetadata,
}: {
  audioBlob: Blob;
  presignedUrl: string;
  appointmentShortId: string;
  recordingMetadata?: RecordingMetadata;
}): Promise<UploadAudioCallbackResponse> {
  try {
    const uploadResponse = await fetch(presignedUrl, {
      method: "PUT",
      body: audioBlob,
    });
    if (!uploadResponse.ok) {
      throw new Error(uploadResponse.statusText ?? "Unknown error");
    }

    logger.info("Successfully uploaded audio recording to S3", {
      recordingMetadata,
      appointmentShortId,
    });
    return {
      status: "success",
      code: UploadAudioCallbackStatusCode.UPLOADED,
      value: uploadResponse,
    };
  } catch (error) {
    logger.error(
      "Failed to upload audio recording to S3",
      {
        recordingMetadata,
        appointmentShortId,
      },
      error as Error,
    );
    return {
      status: "failure",
      code: UploadAudioCallbackStatusCode.FAILED_TO_UPLOAD,
      value: error as Error,
    };
  }
}

const uploadAudioMutex = new Mutex();

export enum UploadAudioCallbackStatusCode {
  UPLOADED = "uploaded",
  MISSING_APPOINTMENT_SHORT_ID = "missing-appointment-short-id",
  FAILED_TO_FETCH_PRE_SIGNED_URL = "failed-to-fetch-pre-signed-url",
  FAILED_TO_UPLOAD = "failed-to-upload",
  SKIPPED = "skipped",
}

type UploadAudioCallbackResponse =
  | UploadAudioRecordingToS3Response
  | CallbackFailure<
      null,
      UploadAudioCallbackStatusCode.MISSING_APPOINTMENT_SHORT_ID
    >
  | CallbackSuccess<null, UploadAudioCallbackStatusCode.SKIPPED>
  | CallbackFailure<
      Error,
      UploadAudioCallbackStatusCode.FAILED_TO_FETCH_PRE_SIGNED_URL
    >;

/**
 * Returns a thread safe callback that uploads an audio blob to S3.
 */
export function useUploadAudioCallback() {
  const getPresignedUrl = useGetPresignedUrlCallback();
  const getAppointmentShortId = useAtomCallback(
    (get) => get(inSessionPatientInformationAtom)?.inSessionAppointmentShortId,
  );

  return useCallback(
    async ({
      audioBlob,
      appointmentShortId: givenAppointmentShortId,
      recordingMetadata,
    }: {
      audioBlob: Blob;
      appointmentShortId?: string;
      recordingMetadata?: RecordingMetadata;
    }): Promise<UploadAudioCallbackResponse> => {
      let appointmentShortId: string | undefined;
      return await uploadAudioMutex.runExclusive(async () => {
        appointmentShortId = givenAppointmentShortId ?? getAppointmentShortId();
        if (!appointmentShortId) {
          logger.error("Missing Appointment short ID", {
            audioRecordingMetadata: recordingMetadata,
          });
          return {
            status: "failure",
            code: UploadAudioCallbackStatusCode.MISSING_APPOINTMENT_SHORT_ID,
            value: null,
          };
        }
        const presignedUrlResponse = await getPresignedUrl(appointmentShortId, {
          contentLength: audioBlob.size,
          contentType: audioBlob.type,
        });
        if (presignedUrlResponse.status === "failure") {
          logger.error("Failed to fetch pre-signed S3 URL", {
            appointmentShortId,
            audioRecordingMetadata: recordingMetadata,
            error: presignedUrlResponse?.value,
          });
          return {
            ...presignedUrlResponse,
            code: UploadAudioCallbackStatusCode.FAILED_TO_FETCH_PRE_SIGNED_URL,
          };
        }

        logger.debug("Uploading audio recording to S3", {
          audioBlobUrl: URL.createObjectURL(audioBlob),
          audioRecordingMetadata: recordingMetadata,
        });
        const presignedUrl = presignedUrlResponse.value?.getPresignedUrl?.url;
        if (!presignedUrl) {
          return {
            status: "success",
            code: UploadAudioCallbackStatusCode.SKIPPED,
            value: null,
          };
        }

        return await gracefullyUploadAudioRecordingToS3({
          audioBlob,
          presignedUrl,
          recordingMetadata,
          appointmentShortId,
        });
      });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );
}
